import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Mic, MicOff } from "lucide-react";
import { useCity } from "@/context/CityContext";
import { toast } from "sonner";
import GenieModal from "@/components/GenieModal";

function GenieMascot({ size = 88 }) {
  return (
    <div style={{ width: size, height: size, position: "relative" }} data-testid="genie-mascot">
      <img src="/genie-bust.png" alt="Eventom Genie" className="w-full h-full object-contain genie-float" data-testid="genie-art-bar" />
    </div>
  );
}

/** Homepage Genie entry point — typing or tapping a chip opens the Genie panel. */
export default function GenieBar() {
  const { city } = useCity();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [runQuery, setRunQuery] = useState("");
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

  const toggleVoice = () => {
    if (!SR) { toast("Voice search available on Chrome and Edge"); return; }
    if (listening) { recRef.current?.stop(); setListening(false); return; }
    const rec = new SR();
    rec.lang = "en-IN"; rec.interimResults = false;
    rec.onresult = (e) => { const t = e.results[0][0].transcript; setQ(t); setListening(false); };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  };

  useEffect(() => () => recRef.current?.stop(), []);

  const ask = (e, preset) => {
    e?.preventDefault();
    const text = (preset || q || "").trim();
    if (!text) { setRunQuery(""); setOpen(true); return; }
    setRunQuery(text);
    setOpen(true);
  };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-4 relative z-10">
      <div className="genie-glow rounded-3xl p-4 sm:p-5" data-testid="genie-bar-container"
        style={{ background: "#171717", border: "1px solid #2A2A2A" }}>
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:block shrink-0"><GenieMascot size={88} /></div>
          <div className="sm:hidden shrink-0"><GenieMascot size={56} /></div>
          <form onSubmit={ask} className="flex-1 min-w-0">
            <div className="chip !bg-skyx/15 !text-skyx !border-skyx/40 mb-2" data-testid="genie-brand"><Sparkles className="w-3 h-3" /> Eventom Genie</div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Sparkles className="w-4 h-4 text-skyx absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="genie-input"
                  placeholder="Tell me what you're looking for…"
                  className="input-dark w-full !pl-10 !py-3 !text-sm" />
              </div>
              <button type="button" onClick={toggleVoice} data-testid="genie-voice-btn"
                className={`icon-circle !w-11 !h-11 shrink-0 ${listening ? "!bg-magenta text-white animate-pulse" : "text-skyx"}`}
                title="Voice search available on Chrome and Edge">
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              <button type="submit" data-testid="genie-submit" className="btn-orange !py-3 !px-4">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="genie-quick-chips">
              {["What's happening this weekend?", "Find concerts", "Find something under ₹1,000"].map((s) => (
                <button key={s} type="button" onClick={(e) => ask(e, s)} data-testid={`genie-chip-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  className="px-3 py-1 rounded-full text-[11px] font-body text-white/75 border border-skyx/25 bg-skyx/8 hover:bg-skyx/20 hover:text-white transition-all active:scale-95">{s}</button>
              ))}
            </div>
            <div className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-white/45">Voice on Chrome & Edge{city ? ` · ${city}` : ""}</div>
          </form>
        </div>
      </div>
      <GenieModal open={open} onClose={() => setOpen(false)} context="fans" initialQuery={runQuery} />
    </section>
  );
}
